import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addReaction,
  getContentForView,
  ingestEvents,
  listContentFeed,
  removeReaction,
} from '@/api/content';
import type {
  ContentEventPayload,
  ContentEventType,
  ContentFeedParams,
  ContentReactionType,
} from '@/types/content';

// SPEC-021: Query + mutation hooks for the mini app Learning Hub.

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useContentFeed(params: ContentFeedParams) {
  return useQuery({
    queryKey: ['miniapp', 'content', 'feed', params],
    queryFn: () => listContentFeed(params),
    staleTime: 30 * 1000,
  });
}

export function useContentDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['miniapp', 'content', 'detail', id],
    queryFn: () => getContentForView(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Reactions (optimistic)
// ---------------------------------------------------------------------------

export function useReactionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      contentId: string;
      type: ContentReactionType;
      action: 'add' | 'remove';
    }) => {
      if (args.action === 'add') {
        await addReaction(args.contentId, args.type);
      } else {
        await removeReaction(args.contentId, args.type);
      }
    },
    onSuccess: (_data, args) => {
      // Invalidate the feed when bookmark state changes so the
      // bookmarked filter refreshes, and the detail so reaction
      // state reflects the mutation on re-fetch.
      qc.invalidateQueries({ queryKey: ['miniapp', 'content', 'feed'] });
      qc.invalidateQueries({ queryKey: ['miniapp', 'content', 'detail', args.contentId] });
    },
  });
}

// ---------------------------------------------------------------------------
// useContentViewer — heartbeat loop with pause-on-blur + flush-on-unmount
// ---------------------------------------------------------------------------

const HEARTBEAT_INTERVAL_MS = 10_000;
const MAX_BATCH_SIZE = 3;

export interface ContentViewerHandle {
  /** Record an event; events batch up before being flushed. */
  recordEvent: (type: ContentEventType, data?: Record<string, unknown>) => void;
  /** Current progress percent (0–100) — callers pass this to their own players too. */
  setProgress: (percent: number) => void;
  /** Mark the content as complete (progress ≥ 90). */
  markComplete: () => void;
}

/**
 * useContentViewer owns the event buffer + heartbeat loop for a single
 * content item. Mount it once from the viewer page. It:
 *
 *   - records an `open` event on mount
 *   - runs a heartbeat every 10s with the last-known progress + elapsed
 *   - pauses the loop while the tab is hidden
 *   - flushes all buffered events on unmount
 *   - batches up to 3 events between network calls
 */
export function useContentViewer(contentId: string | undefined): ContentViewerHandle {
  const progressRef = useRef(0);
  const elapsedRef = useRef(0);
  const visibleRef = useRef(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  const bufferRef = useRef<ContentEventPayload[]>([]);
  const flushTimerRef = useRef<number | null>(null);

  // Flush drains the buffer unconditionally.
  const flush = useCallback(async () => {
    if (!contentId) return;
    const events = bufferRef.current;
    if (events.length === 0) return;
    bufferRef.current = [];
    try {
      await ingestEvents(contentId, { device: 'mini_app', events });
    } catch {
      // Silently drop failed batches — the ~180d content_events retention
      // on the server means we can tolerate occasional lost heartbeats.
    }
  }, [contentId]);

  // Buffer + auto-flush when the batch fills.
  const enqueue = useCallback(
    (event: ContentEventPayload) => {
      bufferRef.current.push(event);
      if (bufferRef.current.length >= MAX_BATCH_SIZE) {
        void flush();
      }
    },
    [flush],
  );

  const recordEvent = useCallback(
    (type: ContentEventType, data?: Record<string, unknown>) => {
      enqueue({
        event_type: type,
        event_data: {
          progress: progressRef.current,
          elapsed_sec: 0,
          ...data,
        },
        occurred_at: new Date().toISOString(),
      });
    },
    [enqueue],
  );

  const setProgress = useCallback((percent: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    if (clamped > progressRef.current) {
      progressRef.current = clamped;
    }
  }, []);

  const markComplete = useCallback(() => {
    progressRef.current = 100;
    enqueue({
      event_type: 'complete',
      event_data: { progress: 100, elapsed_sec: elapsedRef.current },
      occurred_at: new Date().toISOString(),
    });
    elapsedRef.current = 0;
    void flush();
  }, [enqueue, flush]);

  // Mount: record open, start heartbeat, wire visibility + unmount flush.
  useEffect(() => {
    if (!contentId) return;
    // Reset refs for this content item.
    progressRef.current = 0;
    elapsedRef.current = 0;
    bufferRef.current = [];

    enqueue({
      event_type: 'open',
      event_data: { progress: 0, elapsed_sec: 0 },
      occurred_at: new Date().toISOString(),
    });

    const onVisibility = () => {
      const now = document.visibilityState === 'visible';
      if (now && !visibleRef.current) {
        enqueue({
          event_type: 'resume',
          event_data: { progress: progressRef.current },
        });
      } else if (!now && visibleRef.current) {
        enqueue({
          event_type: 'pause',
          event_data: { progress: progressRef.current },
        });
        // Flush the buffer on blur so we don't lose it if the user closes
        // the WebApp without a clean unmount.
        void flush();
      }
      visibleRef.current = now;
    };
    document.addEventListener('visibilitychange', onVisibility);

    flushTimerRef.current = window.setInterval(() => {
      if (!visibleRef.current) return;
      elapsedRef.current += HEARTBEAT_INTERVAL_MS / 1000;
      enqueue({
        event_type: 'heartbeat',
        event_data: {
          progress: progressRef.current,
          elapsed_sec: HEARTBEAT_INTERVAL_MS / 1000,
        },
        occurred_at: new Date().toISOString(),
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (flushTimerRef.current !== null) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
      enqueue({
        event_type: 'close',
        event_data: { progress: progressRef.current, elapsed_sec: elapsedRef.current },
        occurred_at: new Date().toISOString(),
      });
      void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  return useMemo(
    () => ({ recordEvent, setProgress, markComplete }),
    [recordEvent, setProgress, markComplete],
  );
}
